import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { disableCalendarFeed, getCalendarFeed, renewCalendarFeed } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';

/**
 * « Abonnement agenda »: a private ICS URL of the expert's appointments, to
 * subscribe to from any calendar app. It can be copied, opened in the
 * device's calendar (webcal://), replaced (the old one stops working) or
 * switched off.
 */
export default function CalendarFeedDialog({ onClose }) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [url, setUrl] = useState(null); // null = loading, '' = off
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  useDialogFocus(dialogRef, { onEscape: () => { if (!busy) onClose(); } });

  useEffect(() => {
    getCalendarFeed().then((d) => setUrl(d.url || '')).catch(() => { setUrl(''); setError(t('calendarFeed.error')); });
  }, [t]);

  const run = async (action) => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const d = await action();
      setUrl((d && d.url) || '');
    } catch {
      setError(t('calendarFeed.error'));
    } finally {
      setBusy(false);
    }
  };

  const renew = () => {
    if (url && !window.confirm(t('calendarFeed.renewConfirm'))) return;
    run(renewCalendarFeed);
  };
  const disable = () => {
    if (!window.confirm(t('calendarFeed.disableConfirm'))) return;
    run(async () => { await disableCalendarFeed(); return { url: '' }; });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="confirm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div ref={dialogRef} tabIndex={-1} className="confirm-dialog send-dialog" role="dialog"
        aria-modal="true" aria-labelledby="calendar-feed-title">
        <h2 id="calendar-feed-title" className="confirm-title">{t('calendarFeed.title')}</h2>
        <p className="send-dialog-intro">{t('calendarFeed.intro')}</p>

        {url === null && <p role="status">{t('auth.loading')}</p>}

        {url === '' && (
          <button type="button" className="btn btn-primary" onClick={renew} disabled={busy}>
            {t('calendarFeed.create')}
          </button>
        )}

        {url && (
          <>
            <label htmlFor="calendar-feed-url" className="feed-label">{t('calendarFeed.urlLabel')}</label>
            <div className="feed-url">
              <input id="calendar-feed-url" className="form-control" readOnly value={url}
                onFocus={(e) => e.target.select()} />
              <button type="button" className="btn btn-secondary" onClick={copy}>
                {copied ? t('calendarFeed.copied') : t('calendarFeed.copy')}
              </button>
            </div>
            <a className="btn btn-primary feed-open" href={url.replace(/^https?:/, 'webcal:')}>
              {t('calendarFeed.open')}
            </a>
            <ul className="feed-help">
              <li>{t('calendarFeed.helpApple')}</li>
              <li>{t('calendarFeed.helpGoogle')}</li>
              <li>{t('calendarFeed.helpOutlook')}</li>
            </ul>
            <p className="feed-warning">{t('calendarFeed.private')}</p>
          </>
        )}

        {error && <div className="ai-error" role="alert">{error}</div>}

        <div className="confirm-actions">
          {url && (
            <>
              <button type="button" className="btn btn-ghost" onClick={disable} disabled={busy}>
                {t('calendarFeed.disable')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={renew} disabled={busy}>
                {t('calendarFeed.renew')}
              </button>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('calendarFeed.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
